import React, { memo, type ReactElement, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Chip,
  Icon,
  Loader,
  SidePanel,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  SodApplicationGroup,
  SodFieldSet,
  SodRiskScore,
  SodSeverityChip,
  SodTextFieldSet,
} from '../../../../../../../../shared/components';
import { CF_NEU_BLD } from '../../../../../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../../../../shared/hooks';
import { detailsActions } from '../../../../../../../../shared/store/actions';
import { type TrackedViolatingEntitlements } from '../../../../../../data-layer/detailsTypes';

import './ConflictsSP.css';

/*
 * RiskConflictsSP - Side panel for displaying risk conflict details
 *
 * Provides detailed view of selected conflict items including functions,
 * violating entitlements, and their associated properties. Shows trust scores,
 * severity indicators, and comprehensive metadata for selected conflicts.
 */
function RiskConflictsSP(): ReactElement {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();

  const {
    entitlementConflicts: {
      selectedRowData,
      sidepanel: { isSidePanelVisible },
    },
    entitlementRisks: { selectedEntitlementRisk },
    entitlementSummary: { data: selectedEntitlement },
  } = useTypedSelector((state) => state.aag.details);

  const handleSidePanelClose = useCallback(() => {
    dispatch(detailsActions.setEntitlementConflictsSidePanelVisible(false));
    dispatch(detailsActions.setEntitlementConflictsSelectedRow(null));
  }, [dispatch]);

  const renderLayout = useCallback(() => {
    if (!selectedRowData) {
      return null;
    }

    if (selectedRowData.tracker.level === 0) {
      return (
        <Box tag='div' className='Sod-RiskConflictsSP-layout--isFunction'>
          <Box tag='div' className='Sod-RiskConflictsSP-layout--isCountWrapper'>
            <SodTextFieldSet
              label={l(
                'Number of {count, plural, one {Assigned Entitlement} other {Assigned Entitlements}}',
                {
                  count:
                    (selectedRowData as { violatingEntitlementsCount?: number })
                      .violatingEntitlementsCount ?? 0,
                }
              )}
              value={String(
                (selectedRowData as { violatingEntitlementsCount?: number })
                  .violatingEntitlementsCount ?? 0
              )}
            />
            <Tooltip
              trigger={
                <Icon
                  kind='AlertInfoFilledSeparateColors'
                  color='info-500'
                  size='xSmall'
                />
              }
              text={l(
                'Entitlement assigned to the account that led to the violation of this function.'
              )}
              placement='bottom-end'
            />
          </Box>
          <SodTextFieldSet label={l('Value')} value={selectedRowData.name} />
          <SodTextFieldSet
            label={l('Display Name')}
            value={selectedRowData.name || l('Not Provided')}
          />
          <SodTextFieldSet
            label={l('Description')}
            value={selectedEntitlementRisk?.description ?? l('Not Provided')}
            lineLimit={2}
          />
          <SodFieldSet
            label={l('Application')}
            value={
              <SodApplicationGroup
                label={selectedEntitlement?.endpointName ?? ''}
                firstLogo={selectedEntitlement?.endPointLogo ?? undefined}
              />
            }
          />
        </Box>
      );
    }

    if (selectedRowData.tracker.level === 1) {
      const data = selectedRowData as unknown as TrackedViolatingEntitlements;

      const isEntValue = Boolean(
        data.entitlementValue && data.entitlementValue.trim().length !== 0
      );
      const isEntType = Boolean(
        data.entitlementTypeName && data.entitlementTypeName.trim().length !== 0
      );
      const isDescription = Boolean(
        data.description && data.description.trim().length !== 0
      );
      const isGlossary = Boolean(
        data.glossary && data.glossary.trim().length !== 0
      );
      const isCustomProp1 = Boolean(
        data.customProperty1 && data.customProperty1.trim().length !== 0
      );

      return (
        <Box
          tag='div'
          className='Sod-RiskConflictsSP-layout--isViolatingEntitlement'>
          <Box tag='div' className='Sod-RiskConflictsSP-layoutTrust'>
            <Typography kind='h4' color={CF_NEU_BLD}>
              {l('Trust')}
            </Typography>
            <SodRiskScore
              kind='critical'
              size='regular'
              value={data.entitlementTrustScore}
              shouldShowZero
              customChip={
                <Chip
                  color='critical'
                  size='xSmall'
                  label='Low'
                  leftIcon={<Icon kind='Sad' />}
                />
              }
            />
            <span className='Sod-RiskConflictsSP-layoutDivider' />
          </Box>
          <SodTextFieldSet
            label={l('Number of Auth Objects')}
            value={data.object?.length.toString() ?? '0'}
          />
          <SodTextFieldSet
            label={l('Entitlement Value')}
            value={isEntValue ? data.entitlementValue : l('Not Provided')}
          />
          <SodTextFieldSet
            label={l('Entitlement Type')}
            value={isEntType ? data.entitlementTypeName : l('Not Provided')}
          />
          <SodTextFieldSet
            label={l('Description')}
            value={isDescription ? data.description : l('Not Provided')}
          />
          <SodTextFieldSet
            label={l('Glossary')}
            value={isGlossary ? data.glossary : l('Not Provided')}
          />
          <SodTextFieldSet
            label={l('Custom Property 1')}
            value={isCustomProp1 ? data.customProperty1 : l('Not Provided')}
          />
          <Box className='Sod-EntitlementSummary-columns Sod-RiskConflictsSP-layoutCritcalChips'>
            <SodFieldSet
              label={l('Sox Critical')}
              value={<SodSeverityChip text={data.soxSeverity || 'none'} />}
            />
            <SodFieldSet
              label={l('Sys Critical')}
              value={<SodSeverityChip text={data.sysSeverity || 'none'} />}
            />
            <SodFieldSet
              label={l('Privileged')}
              value={
                <SodSeverityChip text={data.privilegedSeverity || 'none'} />
              }
            />
          </Box>
        </Box>
      );
    }

    return (
      <Box className='Sod-Violations-loaderWrapper'>
        <Loader kind='dots' format='inline' color='brand' />
      </Box>
    );
  }, [selectedRowData, l, selectedEntitlementRisk, selectedEntitlement]);

  const header = useMemo(() => {
    if (selectedRowData?.tracker.level === 0) {
      return {
        title: selectedRowData.name || l('Function'),
        description: l('Function'),
        icon: 'JobFunction',
      };
    }

    if (selectedRowData?.tracker.level === 1) {
      const data = selectedRowData as unknown as TrackedViolatingEntitlements;

      return {
        title:
          (data.entitlementDisplayName || data.entitlementValue) ??
          l('Entitlement'),
        description: l('Violating Entitlement'),
        icon: 'Entitlement',
      };
    }

    return {
      title: l('Conflict Details'),
      description: l('Risk Conflict'),
      icon: 'Entitlement',
    };
  }, [l, selectedRowData]);

  const getLayout = useCallback(() => {
    const layoutWrapperClasses = cx(
      'Sod-RiskConflictsSP-layout',
      selectedRowData &&
        `Sod-RiskConflictsSP-layout--isLevel-${selectedRowData.tracker.level}`
    );

    return <Box className={layoutWrapperClasses}>{renderLayout()}</Box>;
  }, [selectedRowData, renderLayout]);

  return (
    <SidePanel
      isOpen={isSidePanelVisible}
      onClose={handleSidePanelClose}
      header={header}
      appIconSize='medium'
      layout={getLayout()}
      isSidePanelNavigatingList
      isPreviousDisabled
      isNextDisabled
      isMobileWidth={false}
      isTabletWidth={false}
      className='Sod-RiskConflictsSP'
      classNames={{
        layoutClassName: 'Sod-RiskConflictsSP-sidePanelLayout',
        headerClassName: 'Sod-RiskConflictsSP-sidePanelHeader',
        innerContentClassName: 'Sod-RiskConflictsSP-sidePanelInnerContent',
        footerClassName: 'Sod-RiskConflictsSP-sidePanelFooter',
      }}
    />
  );
}

export default memo(RiskConflictsSP);
